import { useLocale } from '@e2m-customer-portal/hooks';
import clsx from 'clsx';
import { addHours, addMinutes, eachMinuteOfInterval, isToday, isWeekend, startOfToday } from 'date-fns';
import { Fragment, ReactElement } from 'react';

export interface DeviceTablePlaceholderProps {
  daysInForecast: Date[];
  isFifteenMinutesResolution?: boolean;
}

export function DeviceTablePlaceholder({
  daysInForecast,
  isFifteenMinutesResolution,
}: Readonly<DeviceTablePlaceholderProps>): ReactElement<DeviceTablePlaceholderProps> {
  const { code: locale } = useLocale();

  return (
    <tbody data-testid="device-table-placeholder">
      {eachMinuteOfInterval(
        { start: startOfToday(), end: addHours(startOfToday(), 23) },
        { step: isFifteenMinutesResolution ? 15 : 60 },
      ).map((hourDate, rowIndex) => (
        <tr key={`row-${rowIndex}-${hourDate.toISOString()}`} className="even:bg-grey-50">
          <th
            key={`slice-time-${rowIndex}-${hourDate.toISOString()}`}
            className={clsx(
              'min-w-36 border-0 border-b border-solid pl-6 text-start text-sm font-normal leading-4',
              rowIndex % 4 === 3 ? 'border-b-blue-grey-100' : 'border-b-grey-100',
            )}
            colSpan={2}
          >
            {`${hourDate.toLocaleTimeString(locale, {
              hour: '2-digit',
              minute: '2-digit',
            })} - ${addMinutes(hourDate, isFifteenMinutesResolution ? 15 : 60).toLocaleTimeString(locale, {
              hour: '2-digit',
              minute: '2-digit',
              hourCycle: isFifteenMinutesResolution ? 'h23' : 'h24',
            })}`}
          </th>
          {daysInForecast.map((date, index) => (
            <Fragment key={`slice-${index}-${new Date(date.setHours(hourDate.getHours())).toISOString()}`}>
              <td
                className={clsx(
                  'relative border-0 border-b border-solid p-0 text-center',
                  rowIndex % 4 === 3 ? 'border-b-blue-grey-100' : 'border-b-grey-100',
                  { 'bg-blue-grey-200/5': isWeekend(date) },
                )}
              >
                <div
                  className={clsx(
                    'box-border flex h-6 w-full items-center justify-center gap-1 border-0 border-l border-solid border-grey-100',
                    { 'bg-green-50/40': isToday(date) },
                  )}
                >
                  <div className="animate-loading-1 aspect-square w-1.5 animate-pulse rounded-full bg-black/10" />
                  <div className="animate-loading-2 aspect-square w-1.5 animate-pulse rounded-full bg-black/10" />
                  <div className="animate-loading-3 aspect-square w-1.5 animate-pulse rounded-full bg-black/10" />
                </div>
              </td>
              {index === daysInForecast.length - 1 && (
                <td
                  className={clsx(
                    'border-0 border-b border-solid border-divider',
                    rowIndex % 4 === 3 ? 'border-b-blue-grey-100' : 'border-b-grey-100',
                    { 'bg-blue-grey-200/5': isWeekend(date) },
                  )}
                />
              )}
            </Fragment>
          ))}
        </tr>
      ))}
    </tbody>
  );
}
